import { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { getSocket } from "../../Api/socket";
import { tokenManager } from "../../Api/tokenManager";
import { showAlert } from "../../feedback/feedbackApi";
import { playNotificationSound } from "../../utils/playNotificationSound";
import {
  getPatientNotificationPathOrDefault,
  getProviderNotificationPathOrDefault,
} from "../../utils/notificationRoutes";
import { shouldSuppressChatSocketToast } from "../../utils/notificationForegroundSuppress";

/**
 * Global listener for logged-in users: shows toasts when the server emits
 * notification:new to the active user's Socket.IO room (user:{id}).
 */
function getLiveRole() {
  const patient = tokenManager.getTokenForRole("patient");
  const doctor = tokenManager.getTokenForRole("doctor");
  const hospital = tokenManager.getTokenForRole("hospital");
  const active = tokenManager.getActiveProviderRole();
  if (active === "hospital" && hospital) return "hospital";
  if (active === "doctor" && doctor) return "doctor";
  if (patient) return "patient";
  if (hospital) return "hospital";
  if (doctor) return "doctor";
  return null;
}

function getNotificationActions(type, role, navigate, payload) {
  const merged = { ...payload, type: type || payload?.type };
  if (role === "patient") {
    const path = getPatientNotificationPathOrDefault(merged);
    const t = String(type || merged.type || "").toLowerCase();
    let label = "Open";
    if (path.startsWith("/conversation")) label = "Open chat";
    else if (path.startsWith("/appointment")) label = "Open appointment";
    else if (path.startsWith("/patient-documents")) label = "Open documents";
    else if (t === "prescription_rejected" || t === "prescription_bulk_rejected") label = "Open documents";
    return [{ label, kind: "primary", onClick: () => navigate(path) }];
  }
  if (role === "doctor") {
    const path = getProviderNotificationPathOrDefault(merged, {
      isHospital: false,
      dashboardPath: "/doctor-dashboard",
    });
    const label = path.includes("/doctor-chats")
      ? "Open chat"
      : path.includes("appointments")
        ? "Appointments"
        : path.includes("settings")
          ? "Open settings"
          : "Open";
    return [{ label, kind: "primary", onClick: () => navigate(path) }];
  }
  if (role === "hospital") {
    const path = getProviderNotificationPathOrDefault(merged, {
      isHospital: true,
      dashboardPath: "/hospital-dashboard",
    });
    const label = path.includes("/hospital-chats")
      ? "Open chat"
      : path.includes("appointments")
        ? "Appointments"
        : path.includes("settings")
          ? "Open settings"
          : "Open";
    return [{ label, kind: "primary", onClick: () => navigate(path) }];
  }
  return [];
}

export default function PatientRealtimeNotifications() {
  const navigate = useNavigate();
  const location = useLocation();
  const role = getLiveRole();

  useEffect(() => {
    if (!role) return undefined;
    const socket = getSocket({ role });
    if (!socket) return undefined;

    const onNotify = (payload) => {
      const type = String(payload?.type || "");
      try {
        window.dispatchEvent(new CustomEvent("healbid:patient-notification", { detail: { role, type, payload } }));
      } catch (_) {}

      if (shouldSuppressChatSocketToast(type, payload, role, location)) {
        return;
      }

      const title = String(payload?.title || "").trim();
      const body = String(payload?.body || "").trim();
      const text = [title, body].filter(Boolean).join(" — ") || "You have a new update.";
      const actions = getNotificationActions(type, role, navigate, payload);
      playNotificationSound();
      showAlert(text, { variant: "info", actions });
    };

    /** Server push after admin approval / prescription review — refetch /app/me even if toast type is skipped. */
    const onMeRefresh = () => {
      try {
        window.dispatchEvent(new CustomEvent("healbid:patient-notification", { detail: { role, type: "me_refresh" } }));
      } catch (_) {}
    };

    socket.on("notification:new", onNotify);
    socket.on("me:refresh", onMeRefresh);

    return () => {
      socket.off("notification:new", onNotify);
      socket.off("me:refresh", onMeRefresh);
    };
  }, [navigate, location, role]);

  return null;
}
